import { parseMessage, serializeMessage, type Role, type SignalingMessage, type WaitingSpeaker } from '../../shared/signaling-protocol';
import { backoffMs, RECONNECT_WINDOW_MS } from '../../shared/reconnect';

export type SignalingClientHandlers = {
  onPeerLeft?: (role: Role, peer?: string, final?: boolean) => void;
  onOffer?: (sdp: string, peer?: string) => void;
  onAnswer?: (sdp: string) => void;
  onIce?: (candidate: RTCIceCandidateInit, peer?: string) => void;
  onRoster?: (speakers: WaitingSpeaker[]) => void;
  onAdmitted?: () => void;
  onResumed?: () => void;
  onDismissed?: () => void;
  onKicked?: () => void;
  onRoomFull?: () => void;
  onTakeStart?: (takeId: string, projectId: string, uploadKey: string, uploadToken: string) => void;
  onTakeStop?: (takeId: string) => void;
  onTakeReupload?: (takeId: string, uploadKey: string, uploadToken: string) => void;
  onTakeUploaded?: (takeId: string, ok: boolean, startSeq?: number, peer?: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onReconnecting?: () => void;
  onReconnected?: () => void;
  onError?: (err: Error) => void;
};

function getOrCreateClientId(sessionId: string, role: Role): string {
  const key = `tro-clientId:${sessionId}:${role}`;
  let id = sessionStorage.getItem(key);
  if (!id) { id = crypto.randomUUID(); sessionStorage.setItem(key, id); }
  return id;
}

export class SignalingClient {
  private ws: WebSocket | null = null;
  private readonly clientId: string;
  private live = false;
  private closed = false;            // bewusstes close() → kein Reconnect
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private windowStartMs = 0;         // Beginn des aktuellen Reconnect-Fensters

  constructor(
    private readonly sessionId: string,
    private readonly role: Role,
    private readonly handlers: SignalingClientHandlers,
    private readonly name?: string,
  ) {
    this.clientId = getOrCreateClientId(sessionId, role);
  }

  /** Speaker meldet, ob aktuell eine Live-PeerConnection existiert (geht ins HELLO ein). */
  setLive(v: boolean): void { this.live = v; }

  open(): void {
    this.closed = false;
    this.connect();
  }

  private connect(): void {
    const base = this.role === 'receiver'
      ? `${this.wsBase()}/api/admin/signaling/`
      : `${this.wsBase()}/api/signaling/`;
    const url = `${base}${encodeURIComponent(this.sessionId)}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      const wasReconnect = this.attempt > 0;
      this.attempt = 0;
      this.send({ type: 'HELLO', role: this.role, name: this.name, clientId: this.clientId, live: this.live || undefined });
      this.handlers.onOpen?.();
      if (wasReconnect) this.handlers.onReconnected?.();
    };
    ws.onmessage = (evt) => this.dispatch(evt.data);
    ws.onerror = () => this.handlers.onError?.(new Error('signaling ws error'));
    ws.onclose = (evt) => {
      this.ws = null;
      if (this.closed) { this.handlers.onClose?.(); return; }
      // Kick durch den Host (DO schliesst mit Code 4002) ist TERMINAL — nie reconnecten,
      // sonst klaut sich der gekickte Speaker per HELLO{live}/restore-admit zurück (Geist).
      // Greift unabhängig von der Reihenfolge KICKED-Message vs. onclose.
      if (evt.code === 4002) { this.closed = true; this.handlers.onKicked?.(); return; }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    const now = performance.now();
    if (this.attempt === 0) this.windowStartMs = now;
    if (now - this.windowStartMs > RECONNECT_WINDOW_MS) {
      // Fenster erschöpft → endgültig zu.
      this.handlers.onClose?.();
      return;
    }
    this.handlers.onReconnecting?.();
    const delay = backoffMs(this.attempt++);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  /**
   * Erzwingt SOFORT eine frische Verbindung. Nötig, weil ein gezogenes LAN den
   * alten Socket nicht zuverlässig schliesst (tot-aber-OPEN nach Netzwechsel) →
   * `onclose`/Auto-Reconnect würde u. U. nie feuern, und ein `send()` darüber
   * verschwindet still. Der Aufrufer (Speaker bei PC-Ausfall) bekommt so eine
   * garantiert lebende Leitung → HELLO{live} → DO antwortet RESUMED.
   */
  reconnectNow(): void {
    if (this.closed) return;
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return; // Aufbau läuft schon
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.attempt = 0;
    this.windowStartMs = performance.now();
    const old = this.ws;
    this.ws = null;
    if (old) {
      // Handler abklemmen, damit das (verzögerte) onclose des Zombies keinen
      // zweiten Reconnect anstösst.
      old.onopen = null; old.onmessage = null; old.onerror = null; old.onclose = null;
      try { old.close(); } catch { /* egal, evtl. schon tot */ }
    }
    this.connect();
  }

  send(msg: SignalingMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(serializeMessage(msg));
  }

  // Host-Aktionen
  admit(speakerId: string): void { this.send({ type: 'ADMIT', speakerId }); }
  disconnectSpeaker(speakerId: string): void { this.send({ type: 'DISCONNECT', speakerId }); }
  kick(speakerId: string): void { this.send({ type: 'KICK', speakerId }); }

  // Host → Speaker (peer-getaggt, von der DO geroutet)
  takeStart(peer: string, takeId: string, projectId: string, uploadKey: string, uploadToken: string): void {
    this.send({ type: 'TAKE_START', takeId, projectId, uploadKey, uploadToken, peer });
  }
  takeStop(peer: string, takeId: string): void { this.send({ type: 'TAKE_STOP', takeId, peer }); }
  takeReupload(peer: string, takeId: string, uploadKey: string, uploadToken: string): void {
    this.send({ type: 'TAKE_REUPLOAD', takeId, uploadKey, uploadToken, peer });
  }
  // Speaker → Host
  takeUploaded(takeId: string, ok: boolean, startSeq?: number, error?: string): void {
    this.send({ type: 'TAKE_UPLOADED', takeId, ok, startSeq, error });
  }

  /**
   * Zuverlässiges „ich verlasse die Seite jetzt wirklich" beim Unload — navigator.sendBeacon
   * überlebt das Tab-Schliessen/Reload, ein WS-Frame im pagehide oft nicht. Die DO gibt den
   * Slot sofort frei (final → Host finalisiert den Kanal). Best-effort; Fallback ist die Grace.
   */
  byeBeacon(): void {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    const url = `/api/signaling/${encodeURIComponent(this.sessionId)}/bye`;
    const body = new Blob([JSON.stringify({ clientId: this.clientId })], { type: 'application/json' });
    try { navigator.sendBeacon(url, body); } catch { /* Unload — best effort */ }
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.send({ type: 'BYE' });
    this.ws?.close();
    this.ws = null;
  }

  private wsBase(): string {
    const { protocol, host } = window.location;
    return `${protocol === 'https:' ? 'wss:' : 'ws:'}//${host}`;
  }

  private dispatch(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: SignalingMessage;
    try { msg = parseMessage(raw); } catch { return; }
    switch (msg.type) {
      case 'PEER_LEFT':   this.handlers.onPeerLeft?.(msg.role, msg.peer, msg.final); break;
      case 'OFFER':       this.handlers.onOffer?.(msg.sdp, msg.peer); break;
      case 'ANSWER':      this.handlers.onAnswer?.(msg.sdp); break;
      case 'ICE':         this.handlers.onIce?.(msg.candidate, msg.peer); break;
      case 'ROSTER':      this.handlers.onRoster?.(msg.speakers); break;
      case 'ADMITTED':    this.handlers.onAdmitted?.(); break;
      case 'RESUMED':     this.handlers.onResumed?.(); break;
      case 'DISMISSED':   this.handlers.onDismissed?.(); break;
      case 'KICKED':      this.handlers.onKicked?.(); break;
      case 'ROOM_FULL':   this.handlers.onRoomFull?.(); break;
      case 'BYE':         this.handlers.onClose?.(); break;
      case 'TAKE_START':    this.handlers.onTakeStart?.(msg.takeId, msg.projectId, msg.uploadKey, msg.uploadToken); break;
      case 'TAKE_STOP':     this.handlers.onTakeStop?.(msg.takeId); break;
      case 'TAKE_REUPLOAD': this.handlers.onTakeReupload?.(msg.takeId, msg.uploadKey, msg.uploadToken); break;
      case 'TAKE_UPLOADED': this.handlers.onTakeUploaded?.(msg.takeId, msg.ok, msg.startSeq, msg.peer); break;
    }
  }
}
